import React, { useEffect, useState } from 'react';
import { Edit2, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { updateRestaurant } from '../../api/restaurantsApi';
import { formatPhoneNumber } from '../../utils/formatters';

const TransferNumbersSection = ({ locationId, transferNumber, onSaved }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [number, setNumber] = useState(transferNumber || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEditing) setNumber(transferNumber || '');
  }, [transferNumber, isEditing]);

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      await updateRestaurant(locationId, { transferNumber: number.trim() });
    } catch (saveError) {
      setError(saveError.message || 'Could not save the transfer number. Please try again.');
      setIsSaving(false);
      return;
    }

    setIsEditing(false);
    try {
      await onSaved?.();
    } catch (refreshError) {
      setError('Transfer number saved. Reload the page to see the latest settings.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Transfer Numbers</h3>
            <p className="text-sm text-gray-400">Where callers reach your team</p>
          </div>
          {isEditing ? (
            <div className="flex gap-2">
              <Button
                onClick={() => { setIsEditing(false); setError(''); }}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-700"
                size="sm"
                disabled={isSaving}
              >
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                size="sm"
                disabled={isSaving}
              >
                <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setIsEditing(true)}
              className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
              size="sm"
              disabled={!locationId || isSaving}
            >
              <Edit2 className="w-4 h-4 mr-2" /> Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <label htmlFor="general-transfer-number" className="block text-sm font-medium text-gray-300 mb-2">
          General Transfer Number
        </label>
        {isEditing ? (
          <Input
            id="general-transfer-number"
            type="tel"
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            placeholder="(555) 123-4567"
            disabled={isSaving}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'transfer-number-error' : undefined}
          />
        ) : (
          <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700">
            {transferNumber ? formatPhoneNumber(transferNumber) : 'Not set'}
          </p>
        )}
        {error && <p id="transfer-number-error" role="alert" className="text-sm text-red-400 mt-2">{error}</p>}
      </CardContent>
    </Card>
  );
};

export default TransferNumbersSection;
